import CloseIcon from "@mui/icons-material/Close";
import classNames from "classnames";
import { ReactNode, useEffect } from "react";
import styles from "./style.module.scss";

interface ModalShellProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Small line under the title - the transaction a dialog is about. */
  subtitle?: string;
  /** Caps the dialog's width; receipts need more room than a note. */
  width?: number;
  className?: string;
  children: ReactNode;
}

/**
 * The overlay, dialog surface and header every schoolDetail dialog shares,
 * along with the two behaviours each one has to get right: Escape closes it,
 * and the page behind it must not scroll while it is up.
 */
function ModalShell({
  open,
  onClose,
  title,
  subtitle,
  width = 560,
  className,
  children,
}: ModalShellProps) {
  useEffect(() => {
    if (!open) return undefined;

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div role="presentation" className={styles.overlay} onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{ maxWidth: width }}
        className={classNames(styles.dialog, className)}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.head}>
          <div className={styles.headText}>
            <div className={styles.title}>{title}</div>
            {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
          </div>
          <button
            type="button"
            aria-label="Close"
            className={styles.close}
            onClick={onClose}
          >
            <CloseIcon className={styles.closeIcon} />
          </button>
        </div>

        {children}
      </div>
    </div>
  );
}

export default ModalShell;
